import { useTranslation } from 'react-i18next';
import { PoweredBy } from '@/components/print/PoweredBy';
import { formatCurrency, formatDate } from '@/lib/utils';

/** POS-I192: the stored Z-Report of one closed shift (see backend shifts/z-report.ts). Money is a 2-decimal string. */
export interface ZReportData {
  v: number;
  ref: string;
  business: { name: string; ntn: string | null };
  location: string | null;
  register: string | null;
  cashier: { id: string; name: string };
  openedAt: string;
  closedAt: string;
  sales: {
    count: number;
    gross: string;
    discounts: { order: string; line: string; promotion: string; total: string };
    gst: string;
    furtherTax: string;
    tax: string;
    net: string;
  };
  tips: string;
  tenders: { method: string; count: number; received: string; refunds: string; net: string }[];
  changeGiven: string;
  reconciliation: { received: string; refundsPaid: string; expected: string; difference: string };
  refunds: {
    count: number;
    amount: string;
    byMethod: { method: string; amount: string }[];
    storeCredit: { count: number; amount: string };
  };
  voids: { count: number; amount: string };
  cash: {
    opening: string;
    sales: string;
    refunds: string;
    khata: string;
    drawerIn: string;
    drawerOut: string;
    expected: string;
    counted: string;
    variance: string;
    tolerance: string;
    overTolerance: boolean;
    reason: string | null;
  };
  fbr: { accepted: number; pending: number; failed: number; offline: number };
}

const money = (v: string | number) => formatCurrency(Number(v));

function Row({ label, value, strong }: { label: string; value: React.ReactNode; strong?: boolean }) {
  return (
    <div className={`flex items-start justify-between gap-2 text-sm ${strong ? 'font-semibold' : ''}`}>
      <span>{label}</span>
      <bdi dir="ltr" className="whitespace-nowrap">{value}</bdi>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-1 border-t pt-2">
      <h3 className="text-xs font-bold uppercase tracking-wide">{title}</h3>
      {children}
    </section>
  );
}

/** Printable block. `id="z-report"` is the hook for the 80 mm print rules in index.css. */
export function ZReportView({ report: z }: { report: ZReportData }) {
  const { t } = useTranslation('settings');
  const variance = Number(z.cash.variance);
  const varianceWords =
    Math.abs(variance) < 0.005
      ? t('zrBalanced')
      : `${variance < 0 ? t('zrShort') : t('zrOver')} ${money(Math.abs(variance))}`;
  const method = (m: string) => t(`zrMethod_${m}`, { defaultValue: m.replace(/_/g, ' ') });

  return (
    <div id="z-report" className="space-y-2 rounded-md border bg-background p-3 text-foreground">
      <header className="space-y-0.5 text-center">
        <h2 className="text-base font-bold">{z.business.name}</h2>
        {z.business.ntn && <p className="text-xs">{t('zrNtn')}: <bdi dir="ltr">{z.business.ntn}</bdi></p>}
        <p className="text-sm font-bold">{t('zrTitle')}</p>
        <p className="text-xs">{t('zrRef')}: <bdi dir="ltr">{z.ref}</bdi></p>
      </header>
      <div className="space-y-0.5">
        <Row label={t('zrCashier')} value={z.cashier.name} />
        <Row label={t('zrPlace')} value={[z.location, z.register].filter(Boolean).join(' · ') || '—'} />
        <Row label={t('zrOpened')} value={formatDate(z.openedAt)} />
        <Row label={t('zrClosed')} value={formatDate(z.closedAt)} />
      </div>

      <Section title={t('zrSales')}>
        <Row label={t('zrSalesCount')} value={z.sales.count} />
        <Row label={t('zrGross')} value={money(z.sales.gross)} strong />
        <Row label={t('zrDiscounts')} value={money(z.sales.discounts.total)} />
        <Row label={t('zrGst')} value={money(z.sales.gst)} />
        {Number(z.sales.furtherTax) > 0 && <Row label={t('zrFurtherTax')} value={money(z.sales.furtherTax)} />}
        <Row label={t('zrNet')} value={money(z.sales.net)} />
      </Section>

      <Section title={t('zrTenders')}>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-start text-xs">
              <th className="text-start font-semibold">{t('zrMethod')}</th>
              <th className="text-end font-semibold">{t('zrReceived')}</th>
              <th className="text-end font-semibold">{t('zrRefunded')}</th>
            </tr>
          </thead>
          <tbody>
            {z.tenders.map((r) => (
              <tr key={r.method}>
                <td>{method(r.method)}</td>
                <td className="text-end"><bdi dir="ltr">{money(r.received)}</bdi></td>
                <td className="text-end"><bdi dir="ltr">{Number(r.refunds) > 0 ? money(r.refunds) : '—'}</bdi></td>
              </tr>
            ))}
          </tbody>
        </table>
        <Row label={t('zrChange')} value={money(z.changeGiven)} />
        <Row label={t('zrReceivedTotal')} value={money(z.reconciliation.received)} strong />
      </Section>

      <Section title={t('zrReturns')}>
        <Row label={t('zrReturnsCount')} value={z.refunds.count} />
        <Row label={t('zrRefundsPaid')} value={money(z.refunds.amount)} />
        {z.refunds.storeCredit.count > 0 && (
          <Row label={t('zrStoreCredit')} value={`${z.refunds.storeCredit.count} · ${money(z.refunds.storeCredit.amount)}`} />
        )}
      </Section>

      <Section title={t('zrVoids')}>
        <Row label={t('zrVoidsCount')} value={z.voids.count} />
        <Row label={t('zrVoidsAmount')} value={money(z.voids.amount)} />
      </Section>

      <Section title={t('zrTips')}>
        <Row label={t('zrTipsNote')} value={money(z.tips)} />
      </Section>

      <Section title={t('zrCash')}>
        <Row label={t('zrOpening')} value={money(z.cash.opening)} />
        <Row label={t('zrCashSales')} value={money(z.cash.sales)} />
        {Number(z.cash.refunds) > 0 && <Row label={t('zrCashRefunds')} value={`-${money(z.cash.refunds)}`} />}
        {Number(z.cash.khata) > 0 && <Row label={t('zrKhata')} value={money(z.cash.khata)} />}
        {Number(z.cash.drawerIn) > 0 && <Row label={t('zrDrawerIn')} value={money(z.cash.drawerIn)} />}
        {Number(z.cash.drawerOut) > 0 && <Row label={t('zrDrawerOut')} value={`-${money(z.cash.drawerOut)}`} />}
        <Row label={t('zrExpected')} value={money(z.cash.expected)} strong />
        <Row label={t('zrCounted')} value={money(z.cash.counted)} strong />
        <Row label={t('zrVariance')} value={varianceWords} strong />
        {z.cash.reason && <p className="text-xs">{t('zrReason')}: {z.cash.reason}</p>}
      </Section>

      <Section title={t('zrFbr')}>
        <p className="text-xs">
          {t('dashboardAccepted', { ns: 'common' })}: {z.fbr.accepted} · {t('dashboardPending', { ns: 'common' })}: {z.fbr.pending} ·{' '}
          {t('dashboardFailed', { ns: 'common' })}: {z.fbr.failed}
          {z.fbr.offline > 0 ? ` · ${t('dashboardOffline', { ns: 'common' })}: ${z.fbr.offline}` : ''}
        </p>
      </Section>

      <div className="grid grid-cols-2 gap-4 pt-6 text-center text-xs">
        <div className="border-t pt-1">{t('zrSignCashier')}</div>
        <div className="border-t pt-1">{t('zrSignManager')}</div>
      </div>
      {/* POS-I240: the Z-report slip carries the product line */}
      <PoweredBy />
    </div>
  );
}
